import { Injectable } from '@angular/core';

/**
 * Enables Android/iOS "Install app" (standalone) for tunnel kiosk routes by swapping
 * in a tunnel-scoped web manifest and registering a tunnel service worker.
 */
@Injectable({ providedIn: 'root' })
export class TunnelPwaService {
  private static readonly MAIN_MANIFEST_HREF = '/manifest.webmanifest';
  private static readonly TUNNEL_MANIFEST_HREF = '/tunnel.webmanifest';
  private static readonly SW_SCRIPT = '/tunnel-sw.js';
  private static readonly SW_SCOPE = '/tunnel/';

  private swRegistrationAttempted = false;

  syncForUrl(url: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const isTunnel = this.isTunnelRoute(url);
    this.syncManifestLink(isTunnel);
    this.syncMetaTags(isTunnel);

    if (isTunnel) {
      this.registerTunnelServiceWorker();
    }
  }

  private isTunnelRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/tunnel' || path.startsWith('/tunnel/');
  }

  private syncManifestLink(isTunnel: boolean): void {
    const href = isTunnel
      ? TunnelPwaService.TUNNEL_MANIFEST_HREF
      : TunnelPwaService.MAIN_MANIFEST_HREF;

    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }

    // Manifests are fetched with credentials mode "omit" by default, so on origins
    // behind HTTP Basic auth (the dev/QA environments) the fetch 401s and the install
    // prompt never appears. use-credentials opts the fetch into sending them.
    link.setAttribute('crossorigin', 'use-credentials');

    const currentHref = link.getAttribute('href') ?? '';
    if (currentHref === href) {
      return;
    }

    link.setAttribute('href', href);
  }

  private syncMetaTags(isTunnel: boolean): void {
    this.setMetaTag('theme-color', '#15172E');
    this.setMetaTag('mobile-web-app-capable', 'yes');
    this.setMetaTag('apple-mobile-web-app-capable', 'yes');
    this.setMetaTag('apple-mobile-web-app-title', isTunnel ? 'Way Tunnel' : 'Way Car Wash');
  }

  private setMetaTag(name: string, content: string): void {
    let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  private registerTunnelServiceWorker(): void {
    if (
      this.swRegistrationAttempted ||
      typeof navigator === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }
    this.swRegistrationAttempted = true;

    navigator.serviceWorker
      .register(TunnelPwaService.SW_SCRIPT, { scope: TunnelPwaService.SW_SCOPE })
      .catch(err => {
        console.warn('[TunnelPwa] Service worker registration failed:', err);
        this.swRegistrationAttempted = false;
      });
  }
}
